import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Eye } from "lucide-react";
import api, { mediaUrl, formatApiErrorDetail } from "@/lib/api";
import ImageUpload from "@/components/admin/ImageUpload";
import TagInput from "@/components/admin/TagInput";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = {
  title: "", description: "", story: "", category: "", technique: "", material: "",
  year: "", width: "", height: "", price: "", show_price: false,
  availability: "available", status: "draft", main_image: null, images: [],
  alt_text: "", seo_title: "", seo_description: "", featured: false, tags: [],
};

export default function ArtworkForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [categories, setCategories] = useState([]);
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    api.get("/artwork-categories").then(({ data }) => setCategories(data)).catch(() => {});
    api.get("/admin/artwork-tags").then(({ data }) => setTagSuggestions(data)).catch(() => {});
    if (id) {
      api.get(`/admin/artworks/${id}`).then(({ data }) => {
        setF({ ...EMPTY, ...data, year: data.year ?? "", width: data.width ?? "", height: data.height ?? "", price: data.price ?? "" });
      }).catch(() => toast.error("Nicht gefunden")).finally(() => setLoading(false));
    }
  }, [id]);

  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const save = async (status) => {
    if (!f.title.trim()) { toast.error("Bitte einen Titel eingeben"); return; }
    setSaving(true);
    const payload = {
      ...f,
      status,
      year: f.year ? parseInt(f.year) : null,
      width: f.width ? parseFloat(f.width) : null,
      height: f.height ? parseFloat(f.height) : null,
      price: f.price ? parseFloat(f.price) : null,
    };
    try {
      let saved;
      if (id) saved = (await api.put(`/admin/artworks/${id}`, payload)).data;
      else saved = (await api.post("/admin/artworks", payload)).data;
      toast.success(status === "published" ? "Veröffentlicht" : status === "archived" ? "Archiviert" : "Entwurf gespeichert");
      navigate("/admin/kunstwerke");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  if (loading) return <p className="text-[#9A86B4]">Laden…</p>;

  return (
    <div className="fade-in max-w-3xl">
      <button onClick={() => navigate("/admin/kunstwerke")} className="inline-flex items-center gap-1 text-sm text-[#6E5A82] hover:text-ink mb-6"><ChevronLeft className="w-4 h-4" /> Zurück</button>
      <h1 className="font-serif text-4xl font-light mb-8">{id ? "Kunstwerk bearbeiten" : "Neues Kunstwerk"}</h1>

      <div className="space-y-6 bg-white border border-[#E7D8C6] p-5 sm:p-8">
        <Field label="Titel *"><Input data-testid="art-title" value={f.title} onChange={set("title")} className="rounded-md h-12" /></Field>

        <ImageUpload label="Hauptbild" value={f.main_image} onChange={set("main_image")} />
        <ImageUpload label="Weitere Bilder" value={f.images} onChange={set("images")} multiple />

        <Field label="Beschreibung"><Textarea data-testid="art-description" rows={4} value={f.description} onChange={set("description")} className="rounded-md" /></Field>
        <Field label="Geschichte des Werkes"><Textarea rows={3} value={f.story} onChange={set("story")} className="rounded-md" /></Field>

        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Kategorie">
            <Select value={f.category} onValueChange={set("category")}>
              <SelectTrigger data-testid="art-category" className="rounded-md h-12"><SelectValue placeholder="Wählen…" /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Technik"><Input value={f.technique} onChange={set("technique")} className="rounded-md h-12" placeholder="z.B. Acryl auf Leinwand" /></Field>
          <Field label="Material"><Input value={f.material} onChange={set("material")} className="rounded-md h-12" /></Field>
          <Field label="Jahr"><Input type="number" value={f.year} onChange={set("year")} className="rounded-md h-12" /></Field>
          <Field label="Breite (cm)"><Input type="number" value={f.width} onChange={set("width")} className="rounded-md h-12" /></Field>
          <Field label="Höhe (cm)"><Input type="number" value={f.height} onChange={set("height")} className="rounded-md h-12" /></Field>
        </div>

        <div className="grid sm:grid-cols-2 gap-5 items-end">
          <Field label="Preis (CHF)"><Input type="number" value={f.price} onChange={set("price")} className="rounded-md h-12" /></Field>
          <label className="flex items-center gap-3 h-12">
            <Switch data-testid="art-show-price" checked={f.show_price} onCheckedChange={(v) => setF({ ...f, show_price: v })} />
            <span className="text-sm">Preis öffentlich anzeigen</span>
          </label>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <Field label="Verfügbarkeit">
            <Select value={f.availability} onValueChange={set("availability")}>
              <SelectTrigger data-testid="art-availability" className="rounded-md h-12"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="available">Verfügbar</SelectItem>
                <SelectItem value="reserved">Reserviert</SelectItem>
                <SelectItem value="sold">Verkauft</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <label className="flex items-center gap-3 h-12 sm:mt-6">
            <Switch checked={f.featured} onCheckedChange={(v) => setF({ ...f, featured: v })} />
            <span className="text-sm">Auf Startseite hervorheben</span>
          </label>
        </div>

        <Field label="Alt-Text (Barrierefreiheit)"><Input value={f.alt_text} onChange={set("alt_text")} className="rounded-md h-12" /></Field>

        <Field label="Tags (Themen, Stichworte)">
          <TagInput value={f.tags} onChange={(v) => setF({ ...f, tags: v })} suggestions={tagSuggestions} testId="art-tags-input" />
        </Field>

        <details className="border border-[#F5EBDB] p-4">
          <summary className="cursor-pointer text-sm font-medium">SEO (optional)</summary>
          <div className="mt-4 space-y-4">
            <Field label="SEO-Titel"><Input value={f.seo_title} onChange={set("seo_title")} className="rounded-md h-12" /></Field>
            <Field label="SEO-Beschreibung"><Textarea rows={2} value={f.seo_description} onChange={set("seo_description")} className="rounded-md" /></Field>
          </div>
        </details>
      </div>

      <div className="flex flex-wrap gap-3 mt-6 sticky bottom-0 bg-[#FFF4D6] py-4">
        <Button onClick={() => save("draft")} disabled={saving} variant="outline" data-testid="save-draft" className="rounded-md">Entwurf speichern</Button>
        <Button onClick={() => setPreview(true)} variant="ghost" className="rounded-md"><Eye className="w-4 h-4 mr-2" /> Vorschau</Button>
        <Button onClick={() => save("published")} disabled={saving} data-testid="publish-artwork" className="bg-[#F4C542] text-[#32104F] hover:bg-[#D85CA4] rounded-md">{id ? "Aktualisieren & Veröffentlichen" : "Veröffentlichen"}</Button>
      </div>

      <Dialog open={preview} onOpenChange={setPreview}>
        <DialogContent className="bg-[#FFF9F2] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="sr-only">Vorschau</DialogTitle></DialogHeader>
          <div className="bg-[#F3E7D2]">{f.main_image ? <img src={mediaUrl(f.main_image)} alt="" className="w-full" /> : <div className="aspect-[4/5] bg-[#F3E7D2]" />}</div>
          <div className="pt-4">
            {f.category && <div className="text-xs uppercase tracking-[0.25em] text-[#9A86B4] mb-2">{f.category}</div>}
            <h2 className="font-serif text-4xl font-light">{f.title || "Ohne Titel"}</h2>
            <p className="text-sm text-[#6E5A82] mt-2">{[f.year, f.technique].filter(Boolean).join(" · ")}</p>
            {f.description && <p className="mt-4 text-[#3A1E55] whitespace-pre-wrap">{f.description}</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
